import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Stack } from "@mui/material";
import IconLoader from "@/features/Common/components/IconLoader";
import ScreenWrapper from "../../../Common/ScreenWrapper";
import BMIInfoCard from "../BMIInfoCard";
import BMIChart, { BMIChartDataPoint } from "../BMIChart";
import BMIGauge from "../BMIGauge";
import HereToHelpCard from "../../../HereToHelpCard";
import BMIModal from "../BMIModal";

export interface BMIScreenProps {
  chartData: BMIChartDataPoint[];
  weightKg: number;
  heightCm: number;
  lastUpdated?: string;
  onBack?: () => void;
  onUpdateBmi?: () => void;
  /** Called when BMI is updated; use to trigger get-health-profile refetch */
  onMetricUpdated?: () => void;
}

export default function BMIScreen({
  chartData,
  weightKg,
  heightCm,
  lastUpdated,
  onBack,
  onUpdateBmi,
  onMetricUpdated,
}: BMIScreenProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);

  const handleBack = onBack ?? (() => navigate(-1));

  const currentPoint =
    chartData.find((p) => p.isSelected) ?? chartData[chartData.length - 1];
  const bmi = currentPoint?.bmi ?? 0;

  return (
    <>
      <ScreenWrapper
        onBack={handleBack}
        title={t("HealthHistory.bmi.screen.title")}
        description={t("HealthHistory.bmi.screen.description")}
        onInfoClick={() => setIsInfoModalOpen(true)}
      >
        <Stack gap={2}>
          <BMIInfoCard
            bmi={bmi}
            weightKg={weightKg}
            heightCm={heightCm}
            lastUpdated={lastUpdated}
            onCtaClick={onUpdateBmi}
            onMetricUpdated={onMetricUpdated}
          />
          {chartData.length > 0 && <BMIChart data={chartData} />}
          <BMIGauge value={bmi} />
          <HereToHelpCard
            title={t("HealthHistory.bmi.screen.hereToHelp.title")}
            description={t("HealthHistory.bmi.screen.hereToHelp.description")}
            button={{
              text: t("HealthHistory.bmi.screen.hereToHelp.button"),
              icon: <IconLoader icon="SpeakToADoctorIcon" />,
            }}
          />
        </Stack>
      </ScreenWrapper>

      <BMIModal
        isOpen={isInfoModalOpen}
        onClose={() => setIsInfoModalOpen(false)}
      />
    </>
  );
}
